import React, { useState } from "react"
import dayjs from "dayjs"
import { DateRangePicker } from "rsuite"
import "rsuite/dist/rsuite.min.css"
import { Alert, Button, Card, CardBody, Col, Input, Label, Row, Spinner } from "reactstrap"
import { getPredefinedDateRanges } from "helpers/dateRanges"
import { downloadBusinessMetricsReport, downloadRenewalCustomersReport } from "helpers/backendHelpers/reportExport"

const FORMAT = "YYYY-MM-DD"
const MAX_RANGE_DAYS = 366 * 3

const PAST_PRESETS = [
  { label: "Last 30 days", range: () => [dayjs().subtract(29, "day"), dayjs()] },
  { label: "This month", range: () => [dayjs().startOf("month"), dayjs()] },
  { label: "Last month", range: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
  { label: "Last 3 months", range: () => [dayjs().subtract(3, "month").startOf("month"), dayjs()] },
  { label: "Last 12 months", range: () => [dayjs().subtract(11, "month").startOf("month"), dayjs()] },
  { label: "Last 24 months", range: () => [dayjs().subtract(23, "month").startOf("month"), dayjs()] },
]

const RENEWAL_PRESETS = [
  { label: "Next 30 days", range: () => [dayjs(), dayjs().add(30, "day")] },
  { label: "This month", range: () => [dayjs().startOf("month"), dayjs().endOf("month")] },
  { label: "Next month", range: () => [dayjs().add(1, "month").startOf("month"), dayjs().add(1, "month").endOf("month")] },
  { label: "Next 3 months", range: () => [dayjs(), dayjs().add(3, "month")] },
  { label: "Last 30 days", range: () => [dayjs().subtract(29, "day"), dayjs()] },
  { label: "Last 3 months", range: () => [dayjs().subtract(3, "month").startOf("month"), dayjs()] },
]

// One card layout for every Excel export: pick a range (or a preset) and download.
const ExportCard = ({ title, description, notice, presets, allowFuture, defaultPreset, extraField, onDownload }) => {
  const [initialFrom, initialTo] = presets[defaultPreset].range()
  const [from, setFrom] = useState(initialFrom.format(FORMAT))
  const [to, setTo] = useState(initialTo.format(FORMAT))
  const [extra, setExtra] = useState(extraField?.defaultValue)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const invalidOrder = Boolean(from && to && from > to)
  const missing = !from || !to
  const exceedsLimit = !missing && !invalidOrder && dayjs(to).diff(dayjs(from), "day") > MAX_RANGE_DAYS

  const applyPreset = preset => {
    const [start, end] = preset.range()
    setFrom(start.format(FORMAT))
    setTo(end.format(FORMAT))
    setError("")
  }

  const download = async () => {
    setLoading(true)
    setError("")
    try {
      await onDownload({ from, to, extra })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="mb-3">
          <h5 className="font-size-16 fw-bold mb-1">{title}</h5>
          <p className="text-muted font-size-13 mb-0">{description}</p>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          {presets.map(preset => (
            <Button key={preset.label} size="sm" color="light" type="button" onClick={() => applyPreset(preset)}>
              {preset.label}
            </Button>
          ))}
        </div>

        <Row className="align-items-end g-3">
          <Col md={6} lg={4}>
            <Label className="form-label">Date Range</Label>
            <DateRangePicker
              value={missing ? null : [dayjs(from).toDate(), dayjs(to).toDate()]}
              onChange={value => {
                if (!value) return
                setFrom(dayjs(value[0]).format(FORMAT))
                setTo(dayjs(value[1]).format(FORMAT))
                setError("")
              }}
              format="dd/MM/yyyy"
              placeholder="Select date range"
              showOneCalendar
              cleanable={false}
              block
              ranges={getPredefinedDateRanges({ allowFuture: Boolean(allowFuture) })}
              shouldDisableDate={allowFuture ? undefined : DateRangePicker.afterToday()}
            />
          </Col>
          {extraField && (
            <Col sm={6} md={3}>
              <Label className="form-label">{extraField.label}</Label>
              <Input type="number" min="0" max="365" value={extra} onChange={e => setExtra(e.target.value)} />
            </Col>
          )}
          <Col xs="auto">
            <Button color="primary" type="button" disabled={loading || missing || invalidOrder || exceedsLimit} onClick={download}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" /> Preparing...
                </>
              ) : (
                <>
                  <i className="bx bx-download me-1"></i> Download Excel
                </>
              )}
            </Button>
          </Col>
        </Row>

        {invalidOrder && <div className="text-danger font-size-13 mt-2">From date must not be after To date.</div>}
        {exceedsLimit && <div className="text-danger font-size-13 mt-2">Date range cannot be longer than 3 years.</div>}
        {loading && <div className="text-muted font-size-13 mt-2">Building the report can take a minute or two. Keep this page open.</div>}
        {error && <Alert color="danger" className="mt-3 mb-0">{error}</Alert>}
        {notice && <div className="border-top mt-3 pt-3 text-muted font-size-13">{notice}</div>}
      </CardBody>
    </Card>
  )
}

const ReportExportCards = () => (
  <>
    <ExportCard
      title="Business Metrics"
      description="Revenue, renewals, cohort retention, churn, product usage and customer base. Aggregate numbers only, no customer details."
      presets={PAST_PRESETS}
      defaultPreset={4}
      extraField={{ label: "Renewal grace period (days)", defaultValue: 30 }}
      onDownload={({ from, to, extra }) => downloadBusinessMetricsReport({ from, to, graceDays: extra })}
    />
    <ExportCard
      title="Renewal Customers"
      description="Paid subscriptions expiring in the selected range, and those that expired and have not renewed. Includes customer name, mobile, email and plan."
      presets={RENEWAL_PRESETS}
      defaultPreset={1}
      allowFuture
      notice="This file contains customer contact details. Share it only with authorised staff."
      onDownload={({ from, to }) => downloadRenewalCustomersReport({ from, to })}
    />
  </>
)

export default ReportExportCards
